import { parse } from 'yaml';

import { CONTRACT_RESOURCE, readContract } from './contract';
import { kindOf, kindRulesResource, readPath, readSplit, samplePaths, sampleValues, SplitSpec } from './split-by-field';
import type { Sample } from './types';
import type { ConsoleState } from './store';
import { findDraft, routeSamples } from './store';
import { FLOW_RESOURCE } from './types';
import { drawsMapping, mapsNothing } from './xslt';

/** The values of the split field that, by choice, use the endpoint's main mapping and rules. */
export const SHARED_KINDS_RESOURCE = 'bill-kinds.json';

export interface BillKind {
  value: string;
  name: string;
  /** A branch of its own, the main mapping by choice, or neither yet. */
  uses: 'own' | 'main' | 'none';
  mapped: boolean;
  ruled: boolean;
}

type Node = Record<string, unknown>;
const isNode = (value: unknown): value is Node => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const readable = (payload: string): unknown => {
  try {
    return JSON.parse(payload);
  } catch {
    return undefined;
  }
};

/**
 * The field that tells the docs' example bills apart: short text every example carries, never a
 * number, a date or an id. An id differs on every example and carries digits; a kind repeats, or
 * is a short code of letters. Of those, the one telling most examples apart. A suggestion only;
 * the split decides.
 */
export const suggestKindField = (payloads: unknown[]): string | undefined => {
  if (payloads.length < 2) return undefined;
  const candidates = samplePaths(payloads)
    .filter((path) => !path.includes('[]'))
    .map((path) => ({ path, values: payloads.map((payload) => readPath(payload, path)) }))
    .filter(({ values }) =>
      values.every(
        (value) => value !== undefined && value.trim() !== '' && value.length <= 12 && !/^-?[\d.]+$/.test(value),
      ),
    )
    .map(({ path, values }) => ({
      path,
      distinct: new Set(values).size,
      length: values.reduce((sum, value) => sum + (value?.length ?? 0), 0) / values.length,
      letters: values.every((value) => /^[A-Za-z_]+$/.test(value ?? '')),
    }))
    .filter(
      ({ distinct, length, letters }) => distinct >= 2 && (distinct < payloads.length || (letters && length <= 4)),
    );
  candidates.sort((a, b) => b.distinct - a.distinct || a.length - b.length || a.path.localeCompare(b.path));
  return candidates[0]?.path;
};

/** "Bill Sync - Return" names the Return kind; a sample with no such tail names nothing. */
export const kindNameOf = (sampleName: string): string | undefined =>
  sampleName
    .split(/\s+[-–·]\s+/)
    .slice(1)
    .at(-1)
    ?.trim() || undefined;

/**
 * The split the docs suggest: the field that tells their examples apart, each value named after
 * the first example carrying it. The most common value keeps the main mapping; every other value
 * gets a branch of its own.
 */
export const suggestSplit = (samples: Sample[]): (SplitSpec & { main: string }) | undefined => {
  const docs = samples
    .filter((sample) => sample.source === 'docs')
    .map((sample) => ({ sample, payload: readable(sample.payload) }))
    .filter(({ payload }) => payload !== undefined);
  const path = suggestKindField(docs.map(({ payload }) => payload));
  if (!path) return undefined;
  const values = sampleValues(
    docs.map(({ payload }) => payload),
    path,
  ).map((item) => item.value);
  const nameOf = (value: string) =>
    docs
      .filter(({ payload }) => readPath(payload, path) === value)
      .map(({ sample }) => kindNameOf(sample.name))
      .find(Boolean) ?? value;
  return {
    path,
    branches: values.slice(1).map((value) => ({ value, name: nameOf(value) })),
    otherwise: nameOf(values[0]),
    main: values[0],
  };
};

/** Each branch's value and the mapping file its step runs. */
const branchFiles = (flow: string): Map<string, string> => {
  const files = new Map<string, string>();
  let document: unknown;
  try {
    document = parse(flow);
  } catch {
    return files;
  }
  const visit = (node: unknown): void => {
    if (Array.isArray(node)) node.forEach(visit);
    if (!isNode(node)) return;
    if (isNode(node.choice) && Array.isArray(node.choice.when))
      for (const when of node.choice.when) {
        const expression =
          isNode(when) && isNode(when.expression) && isNode(when.expression.simple)
            ? when.expression.simple.expression
            : '';
        const value = typeof expression === 'string' ? expression.match(/==\s*'([^']*)'/)?.[1] : undefined;
        const step = (Array.isArray((when as Node).steps) ? ((when as Node).steps as unknown[]) : [])
          .map((item) => (isNode(item) && isNode(item.step) ? item.step : undefined))
          .find(Boolean);
        const call = (Array.isArray(step?.steps) ? step.steps : [])
          .map((item) => (isNode(item) && isNode(item.to) ? String(item.to.uri ?? '') : ''))
          .find((uri) => uri.startsWith('xslt-saxon:'));
        if (value !== undefined && call) files.set(value, call.slice('xslt-saxon:'.length).split('?')[0]);
      }
    Object.values(node).forEach(visit);
  };
  visit(document);
  return files;
};

/**
 * Every kind of bill the docs show for this endpoint, and whether each is configured: a mapping
 * drawn and rules written for it, or the main ones by choice. Nothing is required of an endpoint
 * whose docs show one kind.
 */
export interface BillKinds {
  /** The field that tells the kinds apart: the split's, or the one the docs suggest. */
  field?: string;
  /** Whether the flow already tells them apart. */
  split: boolean;
  kinds: BillKind[];
}

export const billKinds = (state: ConsoleState, routeId: string, scope: string): BillKinds => {
  const route = state.routes.find((item) => item.id === routeId);
  const draft = findDraft(state, routeId, scope);
  const docs = routeSamples(state, routeId, 'shared')
    .filter((sample) => sample.source === 'docs')
    .map((sample) => readable(sample.payload))
    .filter((payload) => payload !== undefined);
  const flow = draft?.resources?.[FLOW_RESOURCE] ?? '';
  const split = readSplit(flow);
  const path = split?.path ?? suggestKindField(docs);
  if (!path) return { split: false, kinds: [] };
  const values = sampleValues(docs, path).map((item) => item.value);
  if (values.length < 2) return { field: path, split: Boolean(split), kinds: [] };

  const resources = draft?.resources ?? {};
  const shared = new Set<string>(
    ((readable(resources[SHARED_KINDS_RESOURCE] ?? '') as { shared?: unknown } | undefined)?.shared as string[]) ?? [],
  );
  const files = branchFiles(flow);
  const mainMapped = route?.passThrough === true || drawsMapping(draft?.xslt, draft?.mappings ?? []);
  const mainRuled = Boolean(readContract(resources[CONTRACT_RESOURCE]));

  const kinds = values.map((value) => {
    const branch = split?.branches.find((item) => item.value === value);
    if (branch) {
      const file = files.get(value);
      const xslt = file ? resources[file] : undefined;
      return {
        value,
        name: branch.name,
        uses: 'own' as const,
        mapped: route?.passThrough === true || Boolean(xslt?.trim() && !mapsNothing(xslt)),
        ruled: Boolean(readContract(resources[kindRulesResource(kindOf(branch.name))])),
      };
    }
    return split && shared.has(value)
      ? { value, name: split.otherwise, uses: 'main' as const, mapped: mainMapped, ruled: mainRuled }
      : { value, name: value, uses: 'none' as const, mapped: false, ruled: false };
  });
  return { field: path, split: Boolean(split), kinds };
};

/** What a kind still needs, in words; nothing when it is configured. */
export const kindMissing = (kind: BillKind): string | undefined => {
  if (kind.uses === 'none')
    return `${kind.value} bills have no branch yet. Add ${kind.value} in the split, or leave it unticked to use the main mapping.`;
  if (!kind.mapped) return `${kind.name} bills still need their mapping.`;
  if (!kind.ruled) return `${kind.name} bills still need their rules in "What this vendor may send".`;
  return undefined;
};
